import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';

// components

import { Modal } from 'app-components';
import GIButton from '@common-components/button/GIButton';

// state

import { useAppDispatch, useAppSelector } from '@hooks/index';
import { fetchPermissionDetails } from '@actions/memberPortal/enrollmentsActionCreators';

// network

import { api } from '@modules/common-utils-services/axiosUtil';
import { configs } from '@constants/configs/configs';
import { DIGIT_ONLY_REGEX } from '@utils/validators/regex';
import { logError } from '@utils/tools/ErrorLoggers';
import InputError from '../inputError/InputError';
import { ConditionalRendering } from '../conditional-rendering';

// styles
const StyledBtn = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StyledCenter = styled.div`
    display: flex;
    justify-content: center;
`;

// props

interface AccountActivationEmailModalProps {
    hhId: string;
    hhEmail: string;
    hhPhone: string;
    encHHId?: string;
    onClose: () => void;
}

function AccountActivationEmailModal({ hhId, hhEmail, hhPhone, encHHId, onClose }: AccountActivationEmailModalProps) {

    const dispatch = useAppDispatch();
    const householdFromStore = useAppSelector(state => state.memberEnrollment?.permissions?.household);

    // Refresh the household record from the enrollmentinfo API every time the
    // modal opens so a phone number saved (or removed) on a previous send is
    // reflected here, instead of trusting the stale prop/sessionStorage snapshot.
    // We hold the modal back until this resolves so the email/phone decision is
    // made once, against authoritative data, rather than flashing the wrong
    // option (e.g. hiding the phone field for a number the admin just deleted)
    // and then correcting itself. The app's global spinner covers the brief
    // fetch, so there is no loading UI inside the modal.
    const [isRefreshing, setIsRefreshing] = useState<boolean>(!!encHHId);

    // The permissions.household slice is shared and not keyed to a household, so
    // whatever a previously-viewed member left there is present until our own
    // refetch overwrites it. Only treat the slice as authoritative once the
    // refetch for *this* encHHId has actually succeeded; otherwise we could
    // prefill/submit (or hide the phone field against) another member's record.
    const [freshHouseholdLoaded, setFreshHouseholdLoaded] = useState<boolean>(false);

    // Request-level failures (e.g. api.post rejects) must surface even when the
    // email/phone fields are prefilled and therefore not rendered, otherwise the
    // modal silently stays open with no feedback. Tracked separately from the
    // field-level form errors so it renders unconditionally in the modal body.
    const [requestError, setRequestError] = useState<string>('');

    useEffect(() => {
        if (!encHHId) {
            setIsRefreshing(false);
            return;
        }
        setIsRefreshing(true);
        setFreshHouseholdLoaded(false);
        Promise.resolve(dispatch(fetchPermissionDetails(encHHId, true)))
            .then(result => {
                if (result) {
                    setFreshHouseholdLoaded(true);
                }
            })
            .finally(() => {
                setIsRefreshing(false);
            });
    }, [encHHId, dispatch]);

    const householdEmail = sessionStorage.getItem('householdEmail') || '';
    const householdPhone = sessionStorage.getItem('householdPhone') || '';

    // The freshly-fetched store value is authoritative; the prop and
    // sessionStorage (seeded by the legacy consumerSummaryNavigation.jsp) are
    // fallbacks used until the API response resolves or on non-React entries.
    // Once the household record has actually loaded, an empty phoneNumber means
    // the number was removed (e.g. deleted during impersonation), so we must NOT
    // fall back to the stale prop/sessionStorage snapshot and resurrect it.
    // Require a verified refetch for this household (freshHouseholdLoaded) so a
    // stale slice left by a previously-viewed member (empty encHHId, or a failed
    // fetch that the thunk swallows) is never mistaken for this member's record.
    const householdLoaded =
        freshHouseholdLoaded && !!householdFromStore && Object.keys(householdFromStore).length > 0;
    const storePhone = householdFromStore?.phoneNumber || '';
    const prefillEmail = hhEmail || householdEmail;
    const prefillPhone = householdLoaded ? storePhone : hhPhone || householdPhone;

    // Keep sessionStorage (consumed by the legacy JSP/chat-link flows) in sync
    // with the authoritative household record once it has loaded. If the admin
    // removed the phone during impersonation, drop the stale value so reopening
    // the modal or a legacy entry point does not resurrect the deleted number.
    useEffect(() => {
        if (isRefreshing || !householdLoaded) {
            return;
        }
        if (storePhone) {
            sessionStorage.setItem('householdPhone', storePhone);
        } else {
            sessionStorage.removeItem('householdPhone');
        }
    }, [isRefreshing, householdLoaded, storePhone]);

    const intl = useIntl();

    const { register, handleSubmit, reset, formState: { errors }, setError, clearErrors } = useForm({
        defaultValues: {
            hhEmaillocal: prefillEmail,
            hhPhonelocal: prefillPhone,
        },
    });

    // defaultValues are only read at mount, but prefillPhone/prefillEmail resolve
    // asynchronously once fetchPermissionDetails returns. Re-seed the form when
    // they change so an empty phoneNumber (number removed during impersonation)
    // is reflected instead of resurrecting the stale value cached at first render.
    useEffect(() => {
        reset({
            hhEmaillocal: prefillEmail,
            hhPhonelocal: prefillPhone,
        });
    }, [prefillEmail, prefillPhone, reset]);

    const onClickSendHandler = async (data) => {
        const url = configs.endpoints.SEND_ACTIVATION_LINK;
        const formData = new FormData();
        formData.append('householdId', hhId);

        const email = data?.hhEmaillocal || prefillEmail;
        formData.append('email', email);

        const phoneRaw = data?.hhPhonelocal || prefillPhone;
        const phoneDigits = (phoneRaw || '').replace(DIGIT_ONLY_REGEX, '');
        formData.append('phone', phoneDigits);

        setRequestError('');

        try {
            const res = await api.post(url, formData);
            if (res && !res.data.success) {
                const emailFormError = { type: 'emailExists', message: intl.formatMessage({ id:'common.email.duplicate.error'}) };
                setError('hhEmaillocal', emailFormError);
            }
            else {
                // Keep the rest of the React page (and the sessionStorage-based
                // consumers such as chat links) aware of the just-saved contact
                // details so reopening the modal does not prompt for them again.
                sessionStorage.setItem('householdEmail', email);
                sessionStorage.setItem('householdPhone', phoneDigits);
                if (encHHId) {
                    dispatch(fetchPermissionDetails(encHHId));
                }
                onClose();
                clearErrors();
            }
        }
        catch (error) {
            logError('AccountActivationEmailModal send failed:', { error });
            setRequestError(intl.formatMessage({ id: 'common.technical_issues_body' }));
        }
    };

    const validateEmail=(optionValues)=>{
        const emailRegExp = new RegExp(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/);
        if(optionValues && emailRegExp.test(optionValues)){
            return true;
        } 
        return false;
    };
    const validatePhoneNumber = (optionValues: string | undefined) => {
        if (!optionValues) {
            return false;
        }
        const digitsOnly = optionValues.replace(DIGIT_ONLY_REGEX, '');
        return digitsOnly.length === 10;
    };
    // Hold the modal back until the fresh household resolves; the global spinner
    // is shown during the fetch, so there is nothing to render here yet.
    if (isRefreshing) {
        return null;
    }

    return (
        <form onSubmit={handleSubmit(onClickSendHandler)}>
            <Modal id='account_activation_email_modal'>
                <Modal.Header id='account_activation_email_modal_title' titleId='common.send_activiation_link' onHide={onClose} />
                <Modal.Body>
                    <StyledCenter>
                        <FormattedMessage id='common.send.activation.email' />
                    </StyledCenter>
                    <ConditionalRendering isTrue={!prefillEmail}>
                        <div className='control-group-input-label-center control-group-input-label-align-center'>
                            <div className="grid-row gi-component gi-regular_input">
                                <label className='tablet:grid-col-12 control-label required-true'>
                                    <FormattedMessage id='common.email.address' />
                                </label>                                
                            </div>
                            <div className='tablet:grid-col-6 controls'>
                                <input 
                                    type='text' 
                                    id='email' 
                                    className='input-xlarge' 
                                    size={30} 
                                    {...register('hhEmaillocal', { required: {
                                        value: true,
                                        message: intl.formatMessage({ id:'common.email.address.error'})
                                    }, 
                                    validate: {
                                        validate: (optionValues) => validateEmail(optionValues)  || intl.formatMessage({ id:'common.email.address.error'})
                                    }
                                    
                                    })}
                                />
                                <InputError id="emailExistMessageId" errorMessage={errors?.hhEmaillocal?.message} isShown={errors?.hhEmaillocal}/>
                            </div>
                        </div>
                    </ConditionalRendering>

                    <ConditionalRendering isTrue={!prefillPhone}>
                        <div className='control-group-input-label-center control-group-input-label-align-center'>
                            <div className="grid-row gi-component gi-regular_input">
                                <label className='tablet:grid-col-12 control-label required-true'>
                                    <FormattedMessage id='common.phone.number' />
                                </label>
                            </div>
                            <div className='tablet:grid-col-6 controls'>
                                <input 
                                    type={'text'} 
                                    size={30} 
                                    max={10} 
                                    min={10} 
                                    className='input-xlarge' 
                                    {...register('hhPhonelocal', { required: {
                                        value: true,
                                        message: intl.formatMessage({ id:'common.phone.number.error'})
                                    },
                                    validate: {
                                        validate: (optionValues) => validatePhoneNumber(optionValues) || intl.formatMessage({ id:'common.phone.number.error'})

                                    }
                                    })}
                                />
                                <InputError id="phoneNumberMessageId" errorMessage={errors?.hhPhonelocal?.message} isShown={errors?.hhPhonelocal}/>
                            </div>
                        </div>
                    </ConditionalRendering>
                    
                    <ConditionalRendering isTrue={!!requestError}>
                        <InputError
                            id="activationRequestError"
                            errorMessage={requestError}
                            isShown={true}
                        />
                    </ConditionalRendering>
                </Modal.Body>
                <Modal.Controls>
                    <StyledBtn>
                        <GIButton id='cancel' labelId='common.cancel' variant='secondary' onClickHandler={onClose}></GIButton>
                        <GIButton type={'submit'} id='member_view' labelId='common.send' variant='primary' ></GIButton>
                    </StyledBtn>
                </Modal.Controls>
            </Modal>

        </form>
    );
}

export default AccountActivationEmailModal;
